// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingCart, Plus, Search, Eye, Edit2, Trash2, X,
  Users, FileText, DollarSign, TrendingUp, ChevronRight,
  ArrowUpDown, Download,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { revenueData } from '../data/mockData';

const Sales = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);

  const emptyCustomerForm = { name: '', type: 'On-Premise', contact: '', email: '', phone: '', address: '' };
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);

  const emptyOrderForm = { customerId: '', items: [{ product: '', qty: '', price: '' }], notes: '' };
  const [orderForm, setOrderForm] = useState(emptyOrderForm);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, custRes] = await Promise.all([api.get('/sales/orders'), api.get('/sales/customers')]);
      // Handle paginated response - extract items array
      setOrders(ordersRes.data.items || ordersRes.data);
      setCustomersList(custRes.data.items || custRes.data);
    } catch (err) {
      console.error('Failed to fetch sales data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalRevenue = orders.reduce((sum, o) => sum + o.total, 0);
  const paidOrders = orders.filter((o) => o.paymentStatus === 'Paid').length;

  const filteredOrders = orders.filter((o) =>
    o.customer.toLowerCase().includes(search.toLowerCase()) ||
    o.id.toLowerCase().includes(search.toLowerCase())
  );

  const filteredCustomers = customersList.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.contact.toLowerCase().includes(search.toLowerCase())
  );

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    try {
      if (editCustomer) {
        await api.put(`/sales/customers/${editCustomer.id}`, customerForm);
      } else {
        await api.post('/sales/customers', customerForm);
      }
      fetchData();
      setShowCustomerModal(false);
    } catch (err) {
      console.error('Save customer failed', err);
    }
  };

  const openEditCustomer = (c) => {
    setEditCustomer(c);
    setCustomerForm({ name: c.name, type: c.type, contact: c.contact, email: c.email, phone: c.phone, address: c.address });
    setShowCustomerModal(true);
  };

  const openAddCustomer = () => {
    setEditCustomer(null);
    setCustomerForm(emptyCustomerForm);
    setShowCustomerModal(true);
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    const validItems = orderForm.items.filter((i) => i.product && i.qty && i.price);
    try {
      await api.post('/sales/orders', {
        customerId: orderForm.customerId,
        items: validItems.map((i) => ({ product: i.product, qty: Number(i.qty), price: Number(i.price) })),
        notes: orderForm.notes || '',
      });
      fetchData();
      setShowOrderModal(false);
      setOrderForm(emptyOrderForm);
    } catch (err) {
      console.error('Create order failed', err);
    }
  };

  const addOrderItem = () => {
    setOrderForm({ ...orderForm, items: [...orderForm.items, { product: '', qty: '', price: '' }] });
  };

  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderForm.items];
    newItems[index][field] = value;
    setOrderForm({ ...orderForm, items: newItems });
  };

  const removeOrderItem = (index) => {
    if (orderForm.items.length > 1) {
      setOrderForm({ ...orderForm, items: orderForm.items.filter((_, i) => i !== index) });
    }
  };

  const deleteOrder = async (id) => {
    if (window.confirm('Delete this order?')) {
      try { await api.delete(`/sales/orders/${id}`); fetchData(); } catch (err) { console.error(err); }
    }
  };

  const deleteCustomer = async (id) => {
    if (window.confirm('Delete this customer?')) {
      try { await api.delete(`/sales/customers/${id}`); fetchData(); } catch (err) { console.error(err); }
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales & Distribution</h1>
          <p className="text-sm text-gray-500 mt-1">{orders.length} orders • {customersList.length} customers</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'orders' && (
            <button onClick={() => { setOrderForm(emptyOrderForm); setShowOrderModal(true); }} className="flex items-center gap-2 px-4 py-2.5 bg-[#0891B2] text-white text-sm font-semibold rounded-lg hover:bg-[#0e7490] transition-colors">
              <Plus size={16} /> New Order
            </button>
          )}
          {activeTab === 'customers' && (
            <button onClick={openAddCustomer} className="flex items-center gap-2 px-4 py-2.5 bg-[#0891B2] text-white text-sm font-semibold rounded-lg hover:bg-[#0e7490] transition-colors">
              <Plus size={16} /> Add Customer
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center"><DollarSign size={20} className="text-[#0891B2]" /></div>
          <div><p className="text-xl font-bold text-gray-900">${totalRevenue.toLocaleString()}</p><p className="text-xs text-gray-500">Total Revenue</p></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center"><ShoppingCart size={20} className="text-green-600" /></div>
          <div><p className="text-xl font-bold text-gray-900">{orders.length}</p><p className="text-xs text-gray-500">Total Orders</p></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Users size={20} className="text-blue-600" /></div>
          <div><p className="text-xl font-bold text-gray-900">{customersList.length}</p><p className="text-xs text-gray-500">Customers</p></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp size={20} className="text-emerald-600" /></div>
          <div><p className="text-xl font-bold text-gray-900">{paidOrders}/{orders.length}</p><p className="text-xs text-gray-500">Paid Orders</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        {['orders', 'customers', 'analytics'].map((tab) => (
          <button key={tab} onClick={() => { setActiveTab(tab); setSearch(''); }} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
            activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}>{tab}</button>
        ))}
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-4">
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full ekos-table min-w-[800px]">
              <thead><tr className="bg-gray-50">
                <th>Order ID</th><th>Customer</th><th>Date</th><th>Items</th><th>Total</th><th>Status</th><th>Payment</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="font-medium text-[#0891B2]">{order.id}</td>
                    <td className="font-medium text-gray-900">{order.customer}</td>
                    <td>{order.date}</td>
                    <td>{order.items.length} item{order.items.length !== 1 ? 's' : ''}</td>
                    <td className="font-semibold">${order.total.toLocaleString()}</td>
                    <td><span className={`text-xs font-medium px-2 py-1 rounded-full status-${order.status.toLowerCase()}`}>{order.status}</span></td>
                    <td><span className={`text-xs font-medium px-2 py-1 rounded-full status-${order.paymentStatus.toLowerCase()}`}>{order.paymentStatus}</span></td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => setSelectedOrder(order)} className="p-1.5 text-gray-400 hover:text-[#0891B2] hover:bg-cyan-50 rounded"><Eye size={14} /></button>
                        <button onClick={() => deleteOrder(order.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-4">
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full ekos-table min-w-[800px]">
              <thead><tr className="bg-gray-50">
                <th>Name</th><th>Type</th><th>Contact</th><th>Email</th><th>Orders</th><th>Total Spent</th><th>Last Order</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filteredCustomers.map((c) => (
                  <tr key={c.id}>
                    <td className="font-medium text-gray-900">{c.name}</td>
                    <td><span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      c.type === 'Distributor' ? 'bg-blue-50 text-blue-700' : c.type === 'Retail' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'
                    }`}>{c.type}</span></td>
                    <td>{c.contact}</td>
                    <td className="text-xs">{c.email}</td>
                    <td>{c.totalOrders}</td>
                    <td className="font-semibold">${c.totalSpent.toLocaleString()}</td>
                    <td>{c.lastOrder}</td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => openEditCustomer(c)} className="p-1.5 text-gray-400 hover:text-[#0891B2] hover:bg-cyan-50 rounded"><Edit2 size={14} /></button>
                        <button onClick={() => deleteCustomer(c.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Monthly Revenue</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(val) => [`$${val.toLocaleString()}`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#0891B2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Order {selectedOrder.id}</h3>
              <button onClick={() => setSelectedOrder(null)} className="p-1 hover:bg-gray-100 rounded"><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Customer:</span><p className="font-medium text-gray-900">{selectedOrder.customer}</p></div>
                <div><span className="text-gray-500">Date:</span><p className="font-medium text-gray-900">{selectedOrder.date}</p></div>
                <div><span className="text-gray-500">Status:</span><p><span className={`text-xs font-medium px-2 py-1 rounded-full status-${selectedOrder.status.toLowerCase()}`}>{selectedOrder.status}</span></p></div>
                <div><span className="text-gray-500">Payment:</span><p><span className={`text-xs font-medium px-2 py-1 rounded-full status-${selectedOrder.paymentStatus.toLowerCase()}`}>{selectedOrder.paymentStatus}</span></p></div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Items</h4>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg text-sm">
                      <div><p className="font-medium text-gray-900">{item.product}</p><p className="text-xs text-gray-500">Qty: {item.qty} @ ${item.price.toFixed(2)}</p></div>
                      <p className="font-semibold">${(item.qty * item.price).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="text-sm font-medium text-gray-500">Total</span>
                <span className="text-xl font-bold text-gray-900">${selectedOrder.total.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Order Modal */}
      {showOrderModal && (
        <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">New Order</h3>
              <button onClick={() => setShowOrderModal(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreateOrder} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                <select value={orderForm.customerId} onChange={(e) => setOrderForm({ ...orderForm, customerId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required>
                  <option value="">Select customer...</option>
                  {customersList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Items</label>
                {orderForm.items.map((item, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input placeholder="Product" value={item.product} onChange={(e) => updateOrderItem(i, 'product', e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required />
                    <input type="number" placeholder="Qty" value={item.qty} onChange={(e) => updateOrderItem(i, 'qty', e.target.value)} className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required />
                    <input type="number" step="0.01" placeholder="Price" value={item.price} onChange={(e) => updateOrderItem(i, 'price', e.target.value)} className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required />
                    {orderForm.items.length > 1 && <button type="button" onClick={() => removeOrderItem(i)} className="p-2 text-gray-400 hover:text-red-500"><X size={16} /></button>}
                  </div>
                ))}
                <button type="button" onClick={addOrderItem} className="text-sm text-[#0891B2] font-medium hover:underline">+ Add another item</button>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowOrderModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-[#0891B2] rounded-lg hover:bg-[#0e7490]">Create Order</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Customer Modal */}
      {showCustomerModal && (
        <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{editCustomer ? 'Edit Customer' : 'Add Customer'}</h3>
              <button onClick={() => setShowCustomerModal(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSaveCustomer} className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label><input value={customerForm.name} onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label><select value={customerForm.type} onChange={(e) => setCustomerForm({...customerForm, type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none"><option>On-Premise</option><option>Distributor</option><option>Retail</option></select></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact</label><input value={customerForm.contact} onChange={(e) => setCustomerForm({...customerForm, contact: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input value={customerForm.phone} onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><input value={customerForm.address} onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCustomerModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-[#0891B2] rounded-lg hover:bg-[#0e7490]">{editCustomer ? 'Save Changes' : 'Add Customer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;
